import { z } from 'zod';

import { moroccanPhone } from './phone';

export const CreateVehicleSchema = z.object({
  model: z.string().min(2, 'Model is required').max(100),
  colour: z.string().min(2, 'Colour is required').max(50),
  plateNumber: z.string().min(3, 'Valid plate number is required').max(50),
  seats: z.coerce.number().int().min(1, 'Vehicle must have at least 1 passenger seat').max(8),
  /**
   * RG179. Storage keys answered by `POST /vehicles/documents`, not the URLs
   * of oubay's schema: a driving licence is identity data, and its link is
   * signed on read for the owner and the manager only.
   */
  drivingLicenseKey: z.string().uuid().optional().nullable(),
  carPhotoKey: z.string().uuid().optional().nullable(),
});

/** RG179. The `payload` of `POST /vehicles/documents`. */
export const VehicleDocumentSchema = z.object({
  kind: z.enum(['LICENSE', 'CAR_PHOTO']),
});
export type VehicleDocumentDto = z.infer<typeof VehicleDocumentSchema>;

export const UpdateVehicleSchema = z.object({
  model: z.string().min(2).max(100).optional(),
  colour: z.string().min(2).max(50).optional(),
  plateNumber: z.string().min(3).max(50).optional(),
  seats: z.coerce.number().int().min(1).max(8).optional(),
});

export const CreateRideSchema = z.object({
  /** A SERIAL integer here; oubay's schema had a uuid. */
  vehicleId: z.coerce.number().int().positive(),
  origin: z.string().min(2, 'Origin is required').max(255),
  originLat: z.number().optional().nullable(),
  originLng: z.number().optional().nullable(),
  destination: z.string().min(2, 'Destination is required').max(255),
  destinationLat: z.number().optional().nullable(),
  destinationLng: z.number().optional().nullable(),
  departureDateTime: z.coerce.date().refine((date: Date) => date > new Date(), {
    message: 'Departure time must be in the future',
  }),
  seatsTotal: z.coerce.number().int().min(1, 'Must offer at least 1 seat').max(8),
  pricePerSeat: z.coerce
    .number()
    .min(0, 'Price cannot be negative')
    .max(50, 'Le tarif par place ne peut excéder 50 MAD')
    .default(0),
  contactPhone: z.string().max(30).optional(),
  meetingPoint: z.string().max(255).optional(),
  intermediateStops: z.string().max(255).optional(),
  hasAC: z.coerce.boolean().default(true),
  luggageSize: z.enum(['SMALL', 'MEDIUM', 'LARGE']).default('MEDIUM'),
  ladiesOnly: z.coerce.boolean().default(false),
  isRecurring: z.coerce.boolean().default(false),
  recurringDays: z.string().max(255).optional(),
  recurringEndDate: z.coerce.date().optional().nullable(),
});

/** RG165, oubay c2f3b5a. Every field optional: only what changed is sent. */
export const UpdateRideSchema = z.object({
  departureTime: z.string().optional(),
  departureDateTime: z.coerce.date().optional(),
  pricePerSeat: z.coerce
    .number()
    .min(0, 'Price cannot be negative')
    .max(50, 'Le tarif par place ne peut excéder 50 MAD')
    .optional(),
  meetingPoint: z.string().max(255).optional().nullable(),
  intermediateStops: z.string().max(255).optional().nullable(),
  hasAC: z.coerce.boolean().optional(),
  luggageSize: z.enum(['SMALL', 'MEDIUM', 'LARGE']).optional(),
  ladiesOnly: z.coerce.boolean().optional(),
  recurringDays: z.string().max(255).optional(),
  recurringEndDate: z.coerce.date().optional().nullable(),
});

/** RG167. */
export const CancelRideDateSchema = z.object({
  date: z.string().min(8, 'Date requise au format YYYY-MM-DD'),
  reason: z.string().min(5, "Un motif d'au moins 5 caractères est requis").max(500),
});

/** RG168. */
export const RestoreRideDateSchema = z.object({
  date: z.string().min(8, 'Date requise au format YYYY-MM-DD'),
});

export const RideFilterSchema = z.object({
  origin: z.string().optional(),
  originLat: z.coerce.number().optional().nullable(),
  originLng: z.coerce.number().optional().nullable(),
  destination: z.string().optional(),
  destinationLat: z.coerce.number().optional().nullable(),
  destinationLng: z.coerce.number().optional().nullable(),
  date: z.string().optional(),
  ladiesOnly: z.coerce.boolean().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(50).default(20),
});

export const CancelRideSchema = z.object({
  reason: z.string().max(1000).optional(),
});

export const CancelBookingSchema = z.object({
  reason: z.string().max(1000).optional(),
});

export const WaiveStrikeSchema = z.object({
  reason: z.string().min(5, 'A waiver reason must be provided').max(1000),
});

export const ExtendSuspensionSchema = z.object({
  days: z.coerce.number().int().min(1, 'Must extend by at least 1 day').max(365),
});

export const RespondBookingSchema = z.object({
  action: z.enum(['ACCEPT', 'REJECT']),
  reason: z.string().max(1000).optional(),
});

export const RemovePassengerSchema = z.object({
  reason: z.string().min(10, 'Le motif du retrait doit comporter au moins 10 caractères').max(1000),
});

export const CreateRideReportSchema = z.object({
  /** Optional: the route's `:id` names the ride, as oubay's controller did. */
  rideId: z.coerce.number().int().positive().optional(),
  category: z.string().min(2),
  description: z
    .string()
    .min(10, "La description de l'incident doit comporter au moins 10 caractères")
    .max(3000),
});

export const ResolveRideReportSchema = z.object({
  status: z.enum(['RESOLVED', 'DISMISSED', 'INVESTIGATING']),
  adminNotes: z.string().max(2000).optional(),
});

export const UpdateGenderSchema = z.object({
  gender: z.enum(['MALE', 'FEMALE', 'UNSPECIFIED']),
});

/** RG181. The same number as at sign-up (RG180): `./phone`. */
export const UpdatePhoneSchema = z.object({ phone: moroccanPhone });

/**
 * RG171. The ride is the route's `:id`, so his `rideId` field is gone; the
 * reviewee is an integer where his schema had a uuid.
 */
export const CreateReviewSchema = z.object({
  revieweeId: z.coerce.number().int().positive(),
  rating: z.coerce.number().min(1).max(5),
  comment: z.string().optional(),
});

/** RG161. Absent on a one-off ride. */
export const JoinRideSchema = z.object({
  selectedDays: z.string().max(255).optional().nullable(),
});

/**
 * RG177. His admin controller typed this body inline; the verdicts and the
 * follow-up actions are the ones it switched on.
 */
export const ReviewEvictionSchema = z.object({
  status: z.enum(['JUSTIFIED', 'SANCTIONED_DRIVER', 'SANCTIONED_PASSENGER']),
  notes: z.string().max(2000).optional(),
  action: z
    .enum(['NONE', 'STRIKE_DRIVER', 'SUSPEND_DRIVER', 'STRIKE_PASSENGER', 'SUSPEND_PASSENGER'])
    .optional(),
  targetUserId: z.coerce.number().int().positive().optional(),
  days: z.coerce.number().int().min(1).max(365).optional(),
  reason: z.string().max(1000).optional(),
});

// ============================================================
// CARPOOLING MANAGER - integration Task 12, oubay's admin controller (c205448)
// ============================================================

/** His `parseInt(page) || 1`, and the repository's clamp of `limit` to 1..100. */
const adminPage = z.coerce
  .number()
  .catch(1)
  .transform((n) => Math.max(1, Math.trunc(n) || 1));
const adminLimit = z.coerce
  .number()
  .catch(20)
  .transform((n) => Math.max(1, Math.min(100, Math.trunc(n) || 20)));
/** His `activeOnly === 'true'`: any other value is false. */
const adminFlag = z
  .string()
  .optional()
  .transform((v) => v === 'true');

export const AdminPageQuerySchema = z.object({ page: adminPage, limit: adminLimit });

export const AdminStrikeListQuerySchema = AdminPageQuerySchema.extend({
  userId: z.coerce.number().int().positive().optional(),
  activeOnly: adminFlag,
});

export const AdminSuspensionListQuerySchema = AdminPageQuerySchema.extend({
  activeOnly: adminFlag,
});

export const AdminRideListQuerySchema = AdminPageQuerySchema.extend({
  query: z.string().max(255).optional(),
  ladiesOnly: adminFlag,
});

export const AdminReportListQuerySchema = AdminPageQuerySchema.extend({
  status: z.string().max(20).optional(),
  referenceCode: z.string().max(20).optional(),
});

export const AdminVehicleListQuerySchema = z.object({
  status: z.string().max(20).optional(),
});

/** His `body.days || 7`, `body.reason || 'Décision administrative'`. */
export const AdminSuspendDriverSchema = z.object({
  days: z.coerce.number().int().optional(),
  reason: z.string().max(1000).optional(),
});

export const AdminStrikeDriverSchema = z.object({
  reason: z.string().max(1000).optional(),
});

export const RejectVehicleSchema = z.object({
  reason: z.string().max(1000).optional(),
});

export const AdminCancelRideSchema = z.object({
  reason: z.string().max(1000).optional(),
});
